import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import ActionsGroup from '../../../juristec-ui/core/ActionsGroup';
import Button from '../../../juristec-ui/core/Button';
import CardKpi from '../../../juristec-ui/kpis/grid/CardKpi';
import Select from '../../../juristec-ui/core/SelectNew';
import MainTabs from '../../../juristec-ui/core/Tabs';
import SelectTips from '../../Popovers/SelectTips';
import CustomSide from '../../../juristec-ui/kpis/controllers';
import MiniLoading from '../../../juristec-ui/core/MiniLoading';
import Modifier from './Modifier';

import { useKpiModifiers } from '../../../hooks/modifiers';

import getDefaultStyle from '../../../juristec-ui/kpis/utils/chartTools/getDefaultStyle';
import styleConfigMap from '../../KpiCreate/handleStyleConfig';

import { controlVarDescription, dateOptions, defaultDashStyle } from '../../../options';
import { Gears, Warning } from '../../../juristec-ui/icons';

import {
  MainContainer,
  SideBar,
  PreviewContainer,
  KpiWrapper,
  InputWrapper,
  GenButtonActions,
  InputDoubleWrapper,
  GenChartAlert,
  IconCircle,
  TabContent,
  LoadingWrapper,
  WarningMessage,
} from './styled/SubsetTableCreate.styled';

const CHARTTYPE = 'SubsetTable';

const tempItemInitial = {
  id: 'new',
  name: '',
  status: 'active',
  updatedAt: '',
  type: CHARTTYPE,
  style: { type: CHARTTYPE },
};

const getSorting = (sConfig, firstColumn) => ([
  !['Não ordenado', undefined].includes(sConfig?.SortDataControl)
    ? (sConfig?.ColumnToSortControl ?? firstColumn) : '',
  sConfig?.SortDataControl === 'Decrescente' ? 'desc' : 'asc',
]);

const subsetTableCreateDefaultPropStylesData = {};
const subsetTableCreateDefaultPropFilesOpts = [];

const SubsetTableCreate = ({
  item = undefined,
  hide,
  submit,
  userId = '',
  stylesData = subsetTableCreateDefaultPropStylesData,
  filesOpts = subsetTableCreateDefaultPropFilesOpts,
  getFileColumns,
  genTable,
  getColumnUnique,
}) => {
  const kpiRef = useRef();
  const dataFrameTypes = useRef({});

  const [activeTab, setActiveTab] = useState('tabData');
  const [lockGen, setLockGen] = useState(true);
  const [alertGen, setAlertGen] = useState(false);
  const [kpiLoading, setKpiLoading] = useState(false);

  const [selectedFile, setSelectedFile] = useState({});
  const [fileColumns, setFileColumns] = useState([]);

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedControl, setSelectedControl] = useState({ column: {}, map: {} });

  const [styleConfig, setStyleConfig] = useState({
    ...getDefaultStyle(CHARTTYPE), type: CHARTTYPE,
  });

  const [tempItem, setTempItem] = useState(tempItemInitial);
  const [page, setPage] = useState(0);

  useEffect(() => {
    dataFrameTypes.current = selectedColumns.reduce((aux, cOpt) => {
      aux[cOpt.value] = cOpt.type;
      return aux;
    }, {});
  }, [selectedColumns]);

  const handleModifierChange = () => {
    setLockGen(false);
    setAlertGen(true);
  };

  const {
    modifiers,
    setModifiers,
    addModifier,
    removeModifier,
    editModifier,
    changeModifierOrder,
    resetModifiers,
  } = useKpiModifiers({
    dataFrameTypes: dataFrameTypes.current,
    initialModifiers: {
      filter: item?.meta?.filters ?? [],
      group: item?.meta?.maps ?? [],
    },
    onModifierChange: handleModifierChange,
  });

  useEffect(() => {
    if (item) {
      const loadTable = async () => {
        setLockGen(true);
        setKpiLoading(true);
        setPage(0);
        const resTable = await genTable(
          item.meta,
          item.database,
          0,
          ...getSorting(item.style, item.meta.values[0]?.column ?? ''),
        );
        if (resTable?.data) setTempItem({ ...item, data: resTable.data });
        else setTempItem(item);
        setKpiLoading(false);
      };
      const setParams = async () => {
        const file = filesOpts.find((f) => f.value === item.database);
        if (file?.value !== '') {
          setSelectedFile(file);
          const opts = await getFileColumns(file.value);
          setFileColumns(opts);
          setStyleConfig((s) => ({ ...s, ...item.style }));
          setSelectedColumns(item.meta.values.map((v) => ({
            label: v.column, value: v.column, id: v.column, type: v.type,
          })));
          setSelectedControl(
            item.meta.control?.map((c) => ({
              column: {
                label: c.column, value: c.column, id: c.column, type: c.type,
              },
              map: dateOptions.find((d) => d.value === c.map) ?? {},
            }))?.[0] ?? { column: {}, map: {} },
          );
          setModifiers({
            filter: item.meta.filters ?? [],
            group: item.meta.maps ?? [],
          });
          setActiveTab('tabData');
        }
      };
      loadTable();
      setParams();
    }
  }, [item]);

  const closeAlertGen = () => setAlertGen(false);

  const resetPage = () => {
    setSelectedColumns([]);
    setSelectedControl({ column: {}, map: {} });
    resetModifiers();
    setTempItem(tempItemInitial);
    setActiveTab('tabData');
    setLockGen(true);
  };

  const handleSelectedFile = async (selected) => {
    setSelectedFile(selected);
    if (selected?.value !== '') {
      resetPage();
      const opts = await getFileColumns(selected.value);
      setFileColumns(opts);
    }
  };

  const handleSelectedColumns = (selected) => {
    setLockGen(selected.length <= 0);
    setSelectedColumns(selected);
    setModifiers((m) => ({
      ...m,
      group: m.group.filter((g) => selected.find((s) => s.value === g.column)),
    }));
    resetModifiers(['filter']);
    setAlertGen(true);
  };

  const handleSelectedControlColumn = (selected) => {
    setSelectedControl((c) => ({
      column: selected, map: { ...c.map },
    }));
  };

  const handleSelectedControlMap = (selected) => {
    setSelectedControl((c) => ({
      map: selected, column: { ...c.column },
    }));
  };

  const handleGenChar = async (pg = 0) => {
    setLockGen(true);
    setKpiLoading(true);
    setPage(pg);
    const meta = {
      values: selectedColumns.map((cols) => ({ column: cols.value, map: '', type: cols.type })),
      control: selectedControl.column?.value?.length > 0 ? ([{
        column: selectedControl.column.value,
        map: selectedControl.column.type === 'datetime64[ns]' ? (
          selectedControl.map?.value ?? 'category'
        ) : 'category',
        type: selectedControl.column.type,
      }]) : [],
      filters: modifiers.filter,
      maps: modifiers.group,
      kpi_type: CHARTTYPE,
      database: selectedFile.value,
      fileOwner: selectedFile.owner,
      ownerId: userId,
    };
    const resTable = await genTable(
      meta,
      selectedFile.value,
      pg,
      ...getSorting(styleConfig, selectedColumns[0]?.value ?? ''),
    );
    if (resTable?.data) {
      setTempItem((tItem) => ({
        ...tItem, data: resTable.data, meta, style: styleConfig,
      }));
      if (styleConfig?.CustomSortControl?.kpiCategories?.length > 0) {
        for (let i = 0; i < selectedColumns.length; i += 1) {
          if (!styleConfig.CustomSortControl.kpiCategories.includes(selectedColumns[i].value)) {
            setStyleConfig((p) => ({
              ...p,
              SortValuesControl: p.SortValuesControl === 'Customizado' ? 'Não ordenado' : p.SortValuesControl,
              CustomSortControl: {
                checked: false,
                kpiCategories: [],
              },
            }));
            break;
          }
        }
      }
    } else setLockGen(false);
    setKpiLoading(false);
  };

  const handleGenCharBtn = () => handleGenChar();

  const handlePageChange = (pgState) => {
    const pg = typeof pgState === 'function' ? pgState(page) : pgState;
    handleGenChar(pg);
  };

  const handleStyleConfig = (value, diff) => {
    const sConfig = styleConfigMap(styleConfig, diff, value);
    setStyleConfig(sConfig);
    setTempItem((tItem) => ({
      ...tItem, style: sConfig, name: sConfig?.ShowTitleControl?.checked === true ? sConfig?.TitleControl : '',
    }));
    if (['SortDataControl', 'ColumnToSortControl'].includes(diff)) {
      setLockGen(false);
      setAlertGen(true);
    }
  };

  const getUniqueValues = async (column, dFormat) => (
    getColumnUnique(selectedFile.value, column, dFormat)
  );

  const handleSubmit = () => {
    const body = {
      id: item?.id ?? tempItem.id,
      name: tempItem.name,
      status: tempItem.status,
      updatedAt: tempItem.updatedAt,
      type: tempItem.type,
      meta: {
        values: selectedColumns.map((cols) => ({ column: cols.value, map: '', type: cols.type })),
        control: selectedControl.column?.value?.length > 0 ? ([{
          column: selectedControl.column.value,
          map: selectedControl.column.type === 'datetime64[ns]' ? (
            selectedControl.map?.value ?? 'category'
          ) : 'category',
          type: selectedControl.column.type,
        }]) : [],
        filters: modifiers.filter,
        maps: modifiers.group,
        kpi_type: CHARTTYPE,
        database: selectedFile.value,
        fileOwner: selectedFile.owner,
        ownerId: userId,
      },
    };
    submit(body, styleConfig);
  };

  return (
    <>
      <MainContainer>
        <SideBar>
          <InputWrapper>
            <Select
              label="Selecione o arquivo"
              value={selectedFile}
              options={filesOpts}
              onChange={handleSelectedFile}
              atModal
              searchable
              sortBy="ascending"
            />
          </InputWrapper>
          <GenButtonActions>
            <Button
              size="large"
              variant="outlined"
              onClick={resetPage}
            >
              Resetar
            </Button>
            <Button
              size="large"
              onClick={handleGenCharBtn}
              disabled={lockGen}
            >
              Gerar Tabela
              <Gears />
            </Button>
          </GenButtonActions>
          <MainTabs
            blockers={{
              hasValue: Boolean(selectedColumns.length),
              hasPivotTable: Boolean(tempItem.data),
            }}
            check={{
              tabGroups: ['hasValue', 'hasPivotTable'],
              tabStyles: ['hasValue', 'hasPivotTable'],
              tabFilters: ['hasValue', 'hasPivotTable'],
            }}
            head={[
              { text: 'Dados', id: 'tabData' },
              { text: 'Agrupamento', id: 'tabGroups' },
              { text: 'Filtros', id: 'tabFilters' },
              { text: 'Estilos', id: 'tabStyles' },
            ]}
            active={activeTab}
            setActive={setActiveTab}
          >
            <TabContent>
              <InputDoubleWrapper className={fileColumns.length > 0 ? '' : 'disabled'}>
                <Select
                  label="Colunas"
                  options={fileColumns}
                  value={selectedColumns}
                  onChange={handleSelectedColumns}
                  showExtraSelection
                  multiple
                  stayOpen
                  searchable
                  clearable
                  atModal
                  sortBy="ascending"
                />
              </InputDoubleWrapper>
              <InputDoubleWrapper className={fileColumns.length > 0 ? '' : 'disabled'}>
                <div style={{ position: 'relative' }}>
                  <Select
                    label="Controle (opcional)"
                    onChange={handleSelectedControlColumn}
                    options={selectedColumns}
                    value={selectedControl.column}
                    searchable
                    clearable
                    atModal
                    sortBy="ascending"
                  />
                  <SelectTips
                    description={controlVarDescription}
                    style={{ top: '-5px', right: '2px' }}
                  />
                </div>
                {(selectedControl.column && (selectedControl.column.type === 'datetime64[ns]')) && (
                  <Select
                    label="Formato"
                    onChange={handleSelectedControlMap}
                    value={selectedControl.map}
                    options={dateOptions}
                    searchable
                    clearable
                    atModal
                  />
                )}
              </InputDoubleWrapper>
            </TabContent>
            <TabContent>
              <Modifier
                origin="group"
                modifiers={modifiers}
                columnOpts={selectedColumns}
                getUniqueValues={getUniqueValues}
                addModifier={addModifier}
                removeModifier={removeModifier}
                editModifier={editModifier}
                changeModifierOrder={changeModifierOrder}
                dataFrameTypes={dataFrameTypes.current}
                atModal
              />
            </TabContent>
            <TabContent>
              <Modifier
                origin="filter"
                modifiers={modifiers}
                columnOpts={fileColumns}
                getUniqueValues={getUniqueValues}
                addModifier={addModifier}
                removeModifier={removeModifier}
                editModifier={editModifier}
                changeModifierOrder={changeModifierOrder}
                dataFrameTypes={dataFrameTypes.current}
                atModal
              />
            </TabContent>
            <TabContent>
              <CustomSide
                key={activeTab}
                type={CHARTTYPE}
                config={styleConfig}
                handle={handleStyleConfig}
                data={tempItem.data}
                isGlobalThemeActive={Boolean(stylesData?.useGlobalTheme)}
                isGlobalFontActive={Boolean(stylesData?.useGlobalFont)}
              />
            </TabContent>
          </MainTabs>
        </SideBar>
        <PreviewContainer>
          <KpiWrapper
            style={{
              borderStyle: stylesData?.borderStyle?.line ?? defaultDashStyle.borderLine,
              borderColor: stylesData?.borderStyle?.color ?? defaultDashStyle.borderColor,
              borderWidth: stylesData?.borderStyle?.size ?? defaultDashStyle.borderSize,
              borderRadius: stylesData?.borderStyle?.rounded ?? defaultDashStyle.borderRounded,
              backgroundColor: stylesData?.backgroundColor ?? defaultDashStyle.backgroundColor,
            }}
          >
            {!tempItem.data && !kpiLoading && (
              <></>
            )}
            {!tempItem.data && kpiLoading && (
              <LoadingWrapper>
                <MiniLoading fill="primary" />
              </LoadingWrapper>
            )}
            {Boolean(tempItem.data) && (
              <>
                {tempItem.data && !lockGen && alertGen && (
                  <GenChartAlert>
                    <IconCircle>
                      <Warning />
                    </IconCircle>
                    <h2>Clique em &quot;Gerar Tabela&quot; para aplicar as mudanças</h2>
                    <Button
                      onClick={closeAlertGen}
                      color="secondary"
                      variant="outlined"
                      size="small"
                      style={{ backgroundColor: 'transparent', boxShadow: 'none' }}
                    >
                      Ok
                    </Button>
                  </GenChartAlert>
                )}
                <CardKpi
                  ref={kpiRef}
                  item={tempItem}
                  editPanel={false}
                  isDataLoading={kpiLoading}
                  globalConfig={{
                    ...(stylesData ?? {}),
                    useGlobalTheme: !!stylesData
                      && stylesData.useGlobalTheme
                      && styleConfig?.UseGlobalColor !== false,
                    useGlobalFont: !!stylesData
                      && stylesData.useGlobalFont
                      && styleConfig?.UseGlobalFont !== false,
                  }}
                  pageState={[page, handlePageChange]}
                  collection="kpis"
                />
              </>
            )}
          </KpiWrapper>
        </PreviewContainer>
      </MainContainer>
      <span style={{ fontSize: '10pt' }}>
        <WarningMessage>
          <strong>Atenção: </strong>
        </WarningMessage>
        Subtabelas não podem ser renderizadas nos stories, pois utilizam
        sempre os dados atuais do arquivo de origem. Caso deseje que o
        KPI mostre as informações do story no momento em que o mesmo foi
        criado, escolha outro indicador.
      </span>
      <ActionsGroup>
        <Button
          style={{ margin: '5px' }}
          onClick={hide}
          variant="outlined"
          size="large"
        >
          Cancelar
        </Button>
        <Button
          style={{ margin: '5px' }}
          onClick={handleSubmit}
          size="large"
          disabled={selectedColumns.length <= 0}
        >
          {item ? 'Salvar' : 'Adicionar'}
        </Button>
      </ActionsGroup>
    </>
  );
};

SubsetTableCreate.propTypes = {
  item: PropTypes.shape({
    database: PropTypes.string,
    meta: PropTypes.shape({
      values: PropTypes.arrayOf(PropTypes.shape({
        column: PropTypes.string,
        map: PropTypes.string,
        type: PropTypes.string,
      })),
      control: PropTypes.arrayOf(PropTypes.shape({})),
      filters: PropTypes.arrayOf(PropTypes.shape({})),
      maps: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    style: PropTypes.shape({}),
  }),
  hide: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  userId: PropTypes.string,
  stylesData: PropTypes.shape({
    borderStyle: PropTypes.shape({
      line: PropTypes.string,
      color: PropTypes.string,
      size: PropTypes.number,
      rounded: PropTypes.number,
    }),
    backgroundColor: PropTypes.string,
    useGlobalTheme: PropTypes.bool,
  }),
  filesOpts: PropTypes.arrayOf(PropTypes.shape({})),
  getFileColumns: PropTypes.func.isRequired,
  genTable: PropTypes.func.isRequired,
  getColumnUnique: PropTypes.func.isRequired,
};

export default SubsetTableCreate;
